<template>
	<div class="qplayer-container" ref="playerBox">
		<!-- QNRTPlayer / hls.js / mpegts.js 渲染容器 -->
		<div class="qplayer-media" ref="mediaBox"></div>

		<!-- 原生 video（Safari 原生 HLS） -->
		<video v-if="engine === 'native'" ref="videoEl" class="qplayer-video"
			:autoplay="autoplay" :muted="muted" :controls="controls"
			playsinline webkit-playsinline
			:src="playUrl"></video>

		<!-- 加载中遮罩 -->
		<div class="qplayer-mask" v-if="loading">
			<i class="el-icon-loading"></i>
			<span class="qplayer-mask-text">正在连接视频流...</span>
		</div>

		<!-- 错误遮罩 -->
		<div class="qplayer-mask qplayer-error" v-else-if="errorMsg">
			<i class="el-icon-video-play qplayer-error-icon"></i>
			<span class="qplayer-mask-text">{{ errorMsg }}</span>
			<el-button v-if="errorMsg === playBlockedMsg" type="primary" size="mini"
				@click="resumePlay">点击恢复播放</el-button>
		</div>
	</div>
</template>

<script>
	import { QNRTPlayer, PlayerState } from 'qn-rtplayer-web'
	import Hls from 'hls.js'
	import mpegts from 'mpegts.js'
	import { resolveEngine } from './protocol.js'

	// QNRTPlayer 错误码 → 中文提示
		const QN_ERROR_MAP = {
			20001: '网络错误，请检查网络连接',
			20002: '播放鉴权失败，请检查地址 token 是否有效',
			20003: '媒体流不存在，请确认摄像头是否在线推流',
			20004: '播放请求失败，请检查地址参数',
			20005: '媒体协商失败，请确认视频编码为 H264',
			20006: '连接异常断开，请重试',
			20007: '播放超时，请检查网络后重试',
			30001: '当前浏览器不支持 WebRTC/H264 播放，建议使用 Chrome 浏览器'
		}

	export default {
		name: 'QPlayer',
		props: {
			// 播放地址：七牛云地址（含 qvs-live）/ .m3u8 / .flv
			url: {
				type: String,
				default: ''
			},
			autoplay: {
				type: Boolean,
				default: true
			},
			muted: {
				type: Boolean,
				default: true
			},
			controls: {
				type: Boolean,
				default: true
			}
		},
		data() {
			return {
				engine: 'unknown', // qnrtc / hls / flv / native / unknown
				playUrl: '',
				loading: false,
				errorMsg: '',
				playBlockedMsg: '浏览器自动播放策略拦截了播放，请手动恢复'
			}
		},
		watch: {
			url: {
				immediate: true,
				handler(val) {
					this.stopEngine()
					this.$nextTick(() => {
						this.startPlay(val)
					})
				}
			}
		},
		beforeDestroy() {
			this.stopEngine()
		},
		methods: {
			// ---------- 播放入口 ----------
			startPlay(url) {
				const result = resolveEngine(url)
				this.engine = result.engine
				this.playUrl = result.playUrl
				if (result.error) {
					this.errorMsg = result.error
					this.$emit('error', result.error)
					return
				}
				this.errorMsg = ''
				switch (result.engine) {
					case 'qnrtc':
						this.playByQnrtc(result.playUrl)
						break
					case 'hls':
						this.playByHls(result.playUrl)
						break
					case 'flv':
						this.playByFlv(result.playUrl)
						break
					case 'native':
						this.playByNative(result.playUrl)
						break
				}
			},

			// ---------- 七牛 QNRTPlayer（WebRTC） ----------
			playByQnrtc(url) {
				this.loading = true
				const player = new QNRTPlayer()
				this.qnPlayer = player
				player.init({
					controls: this.controls,
					muted: this.muted,
					playsinline: true
				})
				player.on('playerStateChanged', (state) => {
					// PlayerState: 0 IDLE / 1 INIT / 2 READY / 3 PLAYING / 4 STOP / 5 ERROR
					if (state === PlayerState.STATE_PLAYING) {
						this.loading = false
						this.$emit('playing')
					}
				})
				player.on('error', (err) => {
					this.loading = false
					const code = err && err.code
					const msg = (QN_ERROR_MAP[code] || (err && err.msg) || '播放失败')
					this.errorMsg = msg
					this.$emit('error', this.errorMsg)
				})
				const playPromise = player.play(url, this.$refs.mediaBox)
				if (playPromise && playPromise.catch) {
					playPromise.catch((err) => {
						this.loading = false
						// 浏览器自动播放策略拦截
						if (err && err.name === 'NotAllowedError') {
							this.errorMsg = this.playBlockedMsg
							this.$emit('error', this.errorMsg)
							return
						}
						// error 事件已按错误码给出具体提示（如鉴权失败/流不存在），
						// 此处不再用 promise 的空 message 覆盖，仅在无提示时兜底
						if (!this.errorMsg) {
							this.errorMsg = (err && err.message) || '播放失败'
							this.$emit('error', this.errorMsg)
						}
					})
				}
			},

			// 自动播放被拦截后，用户手动恢复
			resumePlay() {
				if (this.qnPlayer && this.engine === 'qnrtc') {
					this.errorMsg = ''
					this.loading = true
					const p = this.qnPlayer.resume()
					if (p && p.catch) {
						p.catch(() => { this.loading = false })
					}
					return
				}
				// hls/flv/native 场景直接调用 video.play()
				const video = this.$refs.videoEl || this.getInnerVideo()
				if (video) {
					this.errorMsg = ''
					video.play().catch(() => {})
				}
			},

			// ---------- hls.js ----------
			playByHls(url) {
				this.loading = true
				const video = document.createElement('video')
				video.style.cssText = 'width:100%;height:100%;object-fit:contain;'
				video.autoplay = this.autoplay
				video.muted = this.muted
				video.controls = this.controls
				video.setAttribute('playsinline', '')
				video.setAttribute('webkit-playsinline', '')
				this.$refs.mediaBox.appendChild(video)
				this.innerVideo = video

				const hls = new Hls()
				this.hlsPlayer = hls
				hls.loadSource(url)
				hls.attachMedia(video)
				hls.on(Hls.Events.MANIFEST_PARSED, () => {
					this.loading = false
					video.play().then(() => {
						this.$emit('playing')
					}).catch(() => {
						// 自动播放被拦截
						this.errorMsg = this.playBlockedMsg
					})
				})
				hls.on(Hls.Events.ERROR, (event, data) => {
					if (data && data.fatal) {
						this.loading = false
						const msg = 'HLS 播放失败：' + (data.details || data.type)
						this.errorMsg = msg
						this.$emit('error', msg)
					}
				})
			},

			// ---------- mpegts.js（FLV） ----------
			playByFlv(url) {
				if (!mpegts.getFeatureList().mseLivePlayback) {
					const msg = '当前浏览器不支持 FLV 直播播放'
					this.errorMsg = msg
					this.$emit('error', msg)
					return
				}
				this.loading = true
				const video = document.createElement('video')
				video.style.cssText = 'width:100%;height:100%;object-fit:contain;'
				video.autoplay = this.autoplay
				video.muted = this.muted
				video.controls = this.controls
				video.setAttribute('playsinline', '')
				video.setAttribute('webkit-playsinline', '')
				this.$refs.mediaBox.appendChild(video)
				this.innerVideo = video

				const flv = mpegts.createPlayer({
					type: 'flv',
					url: url,
					isLive: true
				})
				this.flvPlayer = flv
				flv.attachMediaElement(video)
				flv.load()
				flv.play().then(() => {
					this.loading = false
					this.$emit('playing')
				}).catch(() => {
					this.loading = false
					this.errorMsg = this.playBlockedMsg
				})
				flv.on(mpegts.Events.ERROR, (errType, errDetail) => {
					this.loading = false
					const msg = 'FLV 播放失败：' + (errDetail || errType)
					this.errorMsg = msg
					this.$emit('error', msg)
				})
			},

			// ---------- 原生 video（Safari HLS） ----------
			playByNative(url) {
				this.loading = true
				this.$nextTick(() => {
					const video = this.$refs.videoEl
					if (!video) return
					video.play().then(() => {
						this.loading = false
						this.$emit('playing')
					}).catch(() => {
						this.loading = false
						this.errorMsg = this.playBlockedMsg
					})
				})
			},

			// 获取内部 video 元素
			getInnerVideo() {
				if (this.$refs.mediaBox) {
					return this.$refs.mediaBox.querySelector('video')
				}
				return null
			},

			// ---------- 统一销毁 ----------
			stopEngine() {
				this.loading = false
				// 七牛播放器：stop + release 彻底释放
				if (this.qnPlayer) {
					try {
						this.qnPlayer.stop()
						this.qnPlayer.release()
					} catch (e) { /* 已释放时的异常忽略 */ }
					this.qnPlayer = null
				}
				// hls.js
				if (this.hlsPlayer) {
					try {
						this.hlsPlayer.destroy()
					} catch (e) { /* 忽略 */ }
					this.hlsPlayer = null
				}
				// mpegts.js
				if (this.flvPlayer) {
					try {
						this.flvPlayer.pause()
						this.flvPlayer.unload()
						this.flvPlayer.detachMediaElement()
						this.flvPlayer.destroy()
					} catch (e) { /* 忽略 */ }
					this.flvPlayer = null
				}
				// 清空动态创建的 video 容器内容
				if (this.$refs.mediaBox) {
					this.$refs.mediaBox.innerHTML = ''
				}
				this.innerVideo = null
			}
		}
	}
</script>

<style scoped>
	.qplayer-container {
		position: relative;
		width: 100%;
		height: 100%;
		background: #000;
		overflow: hidden;
	}

	.qplayer-media {
		width: 100%;
		height: 100%;
	}

	.qplayer-video {
		width: 100%;
		height: 100%;
		object-fit: contain;
		background: #000;
	}

	/* 加载/错误遮罩 */
	.qplayer-mask {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.7);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		color: #fff;
		z-index: 10;
	}

	.qplayer-mask-text {
		margin-top: 12px;
		font-size: 14px;
		text-align: center;
		padding: 0 16px;
		line-height: 1.6;
	}

	.qplayer-error-icon {
		font-size: 42px;
		color: #909399;
	}

	.qplayer-error .el-button {
		margin-top: 16px;
	}

	.qplayer-mask .el-icon-loading {
		font-size: 34px;
		color: #409EFF;
	}
</style>
